/**
 * The lead's submission of a change case's working revision.
 *
 * @module components/governance/ChangeCaseSubmit
 */

import React from "react";

import {
  type ReadinessBlockerEntry,
  ReadinessBlockerList,
} from "@/components/shared/ReadinessBlockerList";
import { Alert, Button, countLabel, Divider, Heading, Icon, useConfirm } from "@/components/ui";
import type { ReadinessBlocker, ReadinessBlockerTarget } from "@/server/types";

import { PROGRAMME_SECTION_LABELS } from "./governance-stations";

export interface ChangeCaseSubmitProps {
  /** No active reviewer is assigned, so the change skips internal review for QA approval. */
  toQa: boolean;
  pending: boolean;
  hasUnsavedChanges: boolean;
  /** What the server's submission gate waits on, or null before it is read. */
  blockers: readonly ReadinessBlocker[] | null;
  /** The editor address a blocker is cleared at, or null for none. */
  hrefFor: (target: ReadinessBlockerTarget) => string | null;
  onSubmit: () => Promise<unknown>;
}

export function ChangeCaseSubmit({
  toQa,
  pending,
  hasUnsavedChanges,
  blockers,
  hrefFor,
  onSubmit,
}: ChangeCaseSubmitProps): React.ReactElement {
  const confirm = useConfirm();
  const requirements: ReadinessBlockerEntry[] | null = blockers
    ? [
        ...blockers.map((blocker) => ({
          key: `${blocker.requirement}-${blocker.message}`,
          id: blocker.requirement,
          message: blocker.message,
          section: blocker.target
            ? (PROGRAMME_SECTION_LABELS[blocker.target.section] ?? blocker.target.section)
            : undefined,
          href: (blocker.target ? hrefFor(blocker.target) : null) ?? undefined,
        })),
      ]
    : null;
  const label = toQa ? "Submit for QA approval" : "Submit for internal review";
  let refusal: string | undefined;
  if (pending) {
    refusal = "Wait for the change in progress to finish.";
  } else if (hasUnsavedChanges) {
    refusal = "Wait for the current changes to save first.";
  } else if (requirements === null) {
    refusal = "Readiness has not been checked yet.";
  } else if (requirements.length > 0) {
    refusal = `${countLabel(requirements.length, "outstanding requirement")} must be completed before submission.`;
  }
  return (
    <div className="mt-4">
      <Divider className="mb-4" />
      {requirements?.length ? (
        <div className="mb-4" data-testid="change-case-submission-requirements">
          <Heading level={3} size="md">
            Before submission ({requirements.length})
          </Heading>
          <ReadinessBlockerList entries={requirements} testIdPrefix="change-case-requirement" />
        </div>
      ) : null}
      <Button
        variant="primary"
        size="sm"
        disabled={refusal !== undefined}
        disabledReason={refusal}
        onClick={async () => {
          // Asked first, as the programme's own submission is: the working
          // revision is fixed once it is sent.
          const confirmed = await confirm({
            title: `${label}?`,
            body: "The working revision is fixed while it is reviewed.",
            confirmLabel: label,
            "data-testid": "submit-change-case-confirmation",
          });
          if (confirmed) {
            await onSubmit();
          }
        }}
        data-testid="submit-change-case"
      >
        <Icon name="paper-plane-tilt" aria-hidden />
        {label}
      </Button>
      {hasUnsavedChanges ? (
        <Alert tone="warning" role="none" className="mt-2">
          Wait for the current changes to finish saving before submission.
        </Alert>
      ) : null}
    </div>
  );
}
